import axios from 'axios';
import Debug from './Debug';
import FT from './FeatureToggles';
import { getUrlParams } from './CommonUtils';
import Session from './Session';
import { getHPIDSignInUrl } from '../../../common/js/utils/CommonUtils';

let _config = {};

let _apis = {
  gandalf: {
    initSelfSignupOC: '/services/user_manager/1.0/user/initSelfsignup',
    abilities: '/services/authz_api_gateway/::0::/rbac/abilities',
    addTrialPlan: '/services/resource_entitlement_service/1.0/tenant/::0::/enableTrial/::1::',
    updateTenantPreference: '/api/2.0/tenant-preferences?tenantId=::0::&key=::1::',
    updateUserPreference: '/services/preferences-service/users?userId=::0::&key=::1::',
    telemetryConsent: '/services/tenant_preference/1.0/preferences/setTenantConsent?tenantId=::0::&key=::1::',
  },
  idm: {
    createCompanyOneCloud: '/api/2.0/tenants/selfSignupCompanyOC',
    companyDetails: '/api/2.0/tenants/::0::',
    getTenantPreference: '/api/2.0/tenant-preferences/list?tenantId=::0::',
    onboardingStatus: '/api/2.0/tenants/::0::/onboardstatus/::1::',
  },
  daasEu: {
    initSelfSignupOC: '/services/user_manager/1.0/user/initSelfsignup',
  },
  daasUs: {
    initSelfSignupOC: '/services/user_manager/1.0/user/initSelfsignup',
  },
  daasAu: {
    initSelfSignupOC: '/services/user_manager/1.0/user/initSelfsignup',
  },
  demo: {
    users: '/users/::0::.json',
    organization: '/organization/::0::.json',
    getIdpToken: '/users/getIdpToken.json',
  },
};

const ServicePlanMap = {
  hp_protect_and_trace_always_on: 'hp_protect_and_trace_always_on',
  starter: 'starter',
};

const ServiceLocale = {
  hp_protect_and_trace_care_pack: 'HP Wolf Protect and Trace',
};

let _countries = {
  all: [],
  byRegion: [],
};

let _languageNameMapping = {
  en: 'English (United States)',
  fr: 'Français',
  ja: '日本語',
  de: 'Deutsch',
  es: 'Español',
  pt_BR: 'Português (Brasil)',
  pt_PT: 'Português (Portugal)',
  nl: 'Nederlandse',
  sv: 'Svenska',
  it: 'Italiano',
  da: 'Dansk',
  fi: 'Suomalainen',
  no: 'Norsk',
  zh_CN: '中文 (简体)',
  zh_TW: '中文 (繁體)',
  ko: '한국어',
};

const CURRENT_SCRIPT = document
  ? document.currentScript
    ? document.currentScript
    : document.querySelector('script.to-run')
      ? document.querySelector('script.to-run')
      : ''
  : '';
const ASSET_BASE = CURRENT_SCRIPT && CURRENT_SCRIPT.src ? CURRENT_SCRIPT.src.replace(/adminx_onboarding\.js(.*)/gi, '') : '';

let Config = {
  setConfig: (config) => {
    _config = Object.assign({}, config);
    Debug.setDebug(_config.debug);
  },
  getBaseUrl: (region = 'daas') => {
    if (region == 'daas') {
      return window.location.origin;
    } else if (region == 'techpulse') {
      return Config.get('appBaseUrl');
    } else if (region == 'daasUs') {
      return _config['wexGASiteUs'];
    } else if (region == 'daasEu') {
      return _config['wexGASiteEu'];
    } else if (region == 'daasAu') {
      return _config['wexGASiteAu'];
    }
  },
  get: (key, arg = false) => {
    switch (key) {
      case 'apiBasePath':
        return _config['apiBasePath'];
      case 'isAdminXApp':
        return (
          window.location.host.includes('adminx.') ||
          window.location.host.includes('admin.') ||
          localStorage.getItem('isAdminx') === 'true'
        );
      case 'assetsURL':
        return Config.get('demo') ? _config['assetsURL'] : window.location.origin + '/techpulse_assets';
      case 'isWPT': {
        const param = getUrlParams();
        const selectedService = param.service ? param.service : localStorage.getItem('selected_service');
        return FT('flip-adminx-to-techpulse') && selectedService && selectedService === 'hp_protect_and_trace_always_on';
      }
      case 'isPendoReady':
        return Config.get('pendoEnabled') && pendo && pendo.isReady && pendo.isReady();
      case 'login_method':
        return localStorage.getItem('login_method');
      case 'serverAddress':
        return window.location.origin;
      case 'adminXProductName':
        return Config.get('isAdminXApp')
          ? (_config[key] ? _config[key] : 'Admin Central') +
              (!FT('wfc-remove-beta', true) && arg ? ' ' + L10n.t('global.beta') : '')
          : _config[key]
            ? _config[key]
            : 'TechPulse';
      case 'supportedLanguages':
        return Array.isArray(FT('wxp-supported-languages')) ? FT('wxp-supported-languages') : ['en'];
      case 'theme': {
        let theme = {
          mode: 'light',
          selectedColor: 'default',
        };

        theme.mode = 'light';
        theme.selectedColor = 'wex';
        // Example: {"selectedColor":"wex","mode":"contrast"}
        let _themeOverride = JSON.parse(localStorage.getItem('wex_theme'));
        if (_themeOverride && _themeOverride.mode) {
          theme.mode = _themeOverride.mode;
        }
        if (_themeOverride && _themeOverride.selectedColor) {
          theme.selectedColor = _themeOverride.selectedColor;
        }

        return theme;
      }
      default:
        return key ? _config[key] : _config;
    }
  },
  setItem: (key, value) => {
    _config[key] = value;
  },
  set: (key, value) => {
    Config.setItem(key, value);
  },
  getLandingUrl: () => {
    let isLocalHost = window.location.origin.indexOf('localhost') !== -1;
    if (isLocalHost) {
      return 'http://localhost:3000/';
    } else {
      return window.location.origin;
    }
  },
  apiPath: (service, api, ...args) => {
    if (!Config.get('apiBasePath') || !Config.get('apiBasePath')[service]) {
      Debug.log('Config.apiPath - Service base path not found: ' + service);
      return '';
    }

    if (!_apis[service] || !_apis[service][api]) {
      Debug.log('Config.apiPath - API path not found: ' + service + '.' + api);
      return '';
    }
    let serviceBaseUrl = Config.get('apiBasePath')[service];
    let path = serviceBaseUrl + _apis[service][api];
    for (let i = 0; i < args.length; i++) {
      path = path.replace('::' + i + '::', args[i]);
    }

    return path;
  },

  getRedirectUrlOnSkip: (type = '') => {
    const locale = L10n.selectedLang || 'en';
    type = type || localStorage.getItem('login_method');
    if (Session.get('onecloud_token')) {
      type = 'onecloud';
    }
    const _basePath = window.location.origin;
    if (Config.get('demo')) {
      return window.location.origin + '?code=test_code&state=test+state';
    }
    if (type == 'aad') {
      return `${_basePath}/services/oauth_handler/hpbp/sign_in?acr_values=urn:hpbp:azure_ad`;
    } else if (type == 'hpid') {
      return _basePath + getHPIDSignInUrl(locale);
    } else if (type === 'onecloud') {
      let email = localStorage.getItem('oc_login_email') || '';
      let providerHref = localStorage.getItem('oc_provider_href') || '';
      return window.location.origin + `/ui/idplogin/?login_hint=${email}&oc_provider_href=${providerHref}`;
    } else {
      return Config.getLandingUrl();
    }
  },

  getLoginUrl: (type = '', service = '', domain = 'daas', initialLogin = true) => {
    type = type ? type : localStorage.getItem('login_method') == 'hpid' ? 'hpid' : 'aad';
    if (Session.get('onecloud_token')) {
      type = 'onecloud';
    }
    const param = getUrlParams();
    const selectedService = param.service
      ? param.service
      : localStorage.getItem('selected_service')
        ? localStorage.getItem('selected_service')
        : service;
    const locale = L10n.selectedLang || 'en';
    const _basePath = Config.getBaseUrl(domain);
    if (Config.get('demo')) {
      return window.location.origin + '?code=test_code&state=test+state';
    }
    // Sign-in returns to /signup so the callback can update onboarding status.
    let _redirectStr = domain == 'techpulse' ? '' : `&redirect_uri=${_basePath}/signup&service=${selectedService}`;
    if (type == 'aad') {
      return `${_basePath}/services/oauth_handler/hpbp/sign_in?acr_values=urn:hpbp:azure_ad` + _redirectStr;
    } else if (type == 'hpid') {
      return _basePath + getHPIDSignInUrl(locale) + _redirectStr;
    } else if (type === 'onecloud') {
      let email = localStorage.getItem('oc_login_email') || '';
      let providerHref = localStorage.getItem('oc_provider_href') || '';
      return window.location.origin + `/ui/idplogin/?login_hint=${email}&oc_provider_href=${providerHref}`;
    } else {
      return Config.getLandingUrl();
    }
  },
  isDemo: (dependency = '') => {
    return Config.get('demo');
  },
  languageOptions: (includeFlag = false) => {
    let ret = [];
    let invalidLanguages = ['nb_NO', 'zh_HANS'];
    let supportedLanguages = Config.get('supportedLanguages');
    let localLanguageNameMapping = _languageNameMapping;
    supportedLanguages.forEach((l) => {
      let index = ret.findIndex((e) => {
        return e.value == l.replace(/-/g, '_');
      });
      if (index < 0 && invalidLanguages.indexOf(l.replace(/-/g, '_')) < 0) {
        let _option = {
          value: l.replace(/-/g, '_'),
          label: localLanguageNameMapping[l.replace(/-/g, '_')],
        };
        if (includeFlag) {
          _option.flag = Config.getFlagName(l);
        }
        ret.push(_option);
      }
    });
    return ret;
  },
  setCountries: (all, byRegion) => {
    _countries.all = all;
    _countries.byRegion = byRegion;
  },
  countryOptions: (byRegion) => {
    return (byRegion ? _countries.byRegion : _countries.all).map((c) => {
      return { label: L10n.t('countries.' + c), value: c };
    });
  },
  getAssetBase: () => {
    let assetBase = ASSET_BASE ? ASSET_BASE : _config.appBaseUrl ? _config.appBaseUrl : '';
    return assetBase;
  },
  load: (url) => {
    return new Promise((resolve, reject) => {
      if (!url) {
        reject();
      }
      axios
        .get(url)
        .then((response) => {
          try {
            if (response.status === 200) {
              Config.setConfig(response.data);
              resolve();
            } else {
              Debug.log('Config.load - Error: ' + response.status);
              Config.setConfig({});
              reject();
            }
          } catch (e) {
            Debug.log('Config.load - Exception thrown during dispatch: ' + e.message);
          }
        })
        .catch((error) => {
          try {
            Config.setConfig({});
            Debug.log('Config.load - Error: ' + error);
            reject();
          } catch (e) {
            Debug.log('Config.load - Exception thrown during reject: ' + e.message);
          }
        });
    });
  },
  getFlagName: (key) => {
    let flag = {
      da: 'dk',
      de: 'de',
      en: 'us',
      es: 'es',
      fi: 'fi',
      fr: 'fr',
      it: 'it',
      ja: 'jp',
      nb_NO: 'no',
      no: 'no',
      nl: 'nl',
      pt_BR: 'br',
      pt_PT: 'pt',
      sv: 'se',
      zh_CN: 'cn',
      zh_HANS: 'zh_HANS',
      'nb-NO': 'no',
      'pt-BR': 'br',
      'pt-PT': 'pt',
      'zh-CN': 'cn',
      'zh-HANS': 'zh_HANS',
      ko: 'ko',
    };
    return flag[key];
  },
  hpTechHpidLangMapping: {
    da: 'da_DK',
    de: 'de_DE',
    en: 'en_US',
    es: 'es_ES',
    fi: 'fi_FI',
    fr: 'fr_FR',
    it: 'it_IT',
    ja: 'ja_JP',
    nb_NO: 'nb_NO',
    no: 'nb_NO',
    nl: 'nl_NL',
    pt_BR: 'pt_BR',
    pt_PT: 'pt_PT',
    sv: 'sv_SE',
    zh_CN: 'zh_CN',
    zh_HANS: 'zh_TW',
    ko: 'ko',
  },
  redirectToUrl: (url) => {
    Debug.log('Redirecting from DaaS UI to ' + url);
    window.location.href = url;
  },

  setCurrentService: (service) => {
    if (!service) {
      return;
    }
    if (ServicePlanMap[service]) {
      localStorage.setItem('selected_service', ServicePlanMap[service]);
      localStorage.setItem('service_name', ServiceLocale[service]);
    } else {
      localStorage.removeItem('selected_service');
      localStorage.removeItem('service_name');
    }
  },
};

export default Config;
